/**
 * Detect three taps within a short window (kiosk status-overlay gesture).
 * Clicks and remote OK/Enter both count. A click synthesized by the WebView
 * right after OK is not counted twice.
 */

import { isRemoteSelectKey } from '@/helpers/androidRemoteKey'

/** Long enough for three OK presses on an IR/BT remote, still a deliberate gesture. */
export const TRIPLE_TAP_WINDOW_MS = 1600 as const

/** Drop the click the WebView fires for the same OK that already counted as a key. */
export const REMOTE_SELECT_CLICK_DEDUPE_MS = 450 as const

/** Set when a triple-tap fires; widget click-forward overlays should skip that click. */
let suppressNextClickForward = false

export function markSuppressClickForwardAfterTripleTap(): void {
  suppressNextClickForward = true
}

/** Returns true once after a triple-tap; clears the flag. */
export function consumeSuppressClickForwardAfterTripleTap(): boolean {
  if (!suppressNextClickForward) {
    return false
  }
  suppressNextClickForward = false
  return true
}

export type TripleTapDetector = {
  /** Record a tap; returns true when this tap completed a triple. */
  noteTap: (nowMs?: number) => boolean
  reset: () => void
}

export function createTripleTapDetector(options?: {
  windowMs?: number
  onTripleTap?: () => void
  /** When true (default), mark click-forward suppress for widget overlays. */
  suppressClickForward?: boolean
}): TripleTapDetector {
  const windowMs = options?.windowMs ?? TRIPLE_TAP_WINDOW_MS
  const suppressForward = options?.suppressClickForward !== false
  const taps: number[] = []

  return {
    noteTap(nowMs = Date.now()) {
      taps.push(nowMs)
      while (taps.length > 0 && nowMs - taps[0]! > windowMs) {
        taps.shift()
      }
      if (taps.length < 3) {
        return false
      }
      taps.length = 0
      if (suppressForward) {
        markSuppressClickForwardAfterTripleTap()
      }
      options?.onTripleTap?.()
      return true
    },
    reset() {
      taps.length = 0
    }
  }
}

/** Test helper. */
export function resetTripleTapClickForwardSuppressForTests(): void {
  suppressNextClickForward = false
}

type RemoteSelectLike = { key?: string, keyCode?: number, repeat?: boolean }

/**
 * One counter for screen taps and remote OK. While `isSuppressed` (kiosk PIN open),
 * input resets the sequence and does not toggle the panel.
 * While the panel is already open, remote OK drives the toolbar instead of this gesture,
 * so it does not count — otherwise the OK that activates Settings can also close the panel.
 */
export function createStatusOverlayGesture(options: {
  onTripleTap: () => void
  isSuppressed: () => boolean
  isOverlayOpen?: () => boolean
}) {
  const taps = createTripleTapDetector({ onTripleTap: options.onTripleTap })
  const overlayOpen = options.isOverlayOpen ?? (() => false)
  let ignoreClickUntil = 0

  return {
    onClick(now = Date.now()) {
      if (options.isSuppressed()) {
        taps.reset()
        return
      }
      if (now < ignoreClickUntil) {
        return
      }
      taps.noteTap(now)
    },
    onKeyDown(evt: RemoteSelectLike, now = Date.now()) {
      if (!isRemoteSelectKey(evt)) {
        return
      }
      if (options.isSuppressed() || overlayOpen()) {
        taps.reset()
        ignoreClickUntil = now + REMOTE_SELECT_CLICK_DEDUPE_MS
        return
      }
      ignoreClickUntil = now + REMOTE_SELECT_CLICK_DEDUPE_MS
      taps.noteTap(now)
    },
    reset() {
      taps.reset()
      ignoreClickUntil = 0
    }
  }
}
